import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Header: React.FC = () => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const isActive = (path: string) => location.pathname === path;
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white/95 backdrop-blur-md shadow-card' : 'bg-transparent'
    }`}>
      <div className="max-w-content mx-auto px-6">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center group">
              <img 
                src="/clawbrand_logo_color.png" 
                alt="ClawBrand AI" 
                className="h-10 w-auto max-w-full group-hover:scale-110 transition-transform duration-200 object-contain sm:h-12"
              />
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            <Link
              to="/"
              className={`${
                isActive('/') 
                  ? 'text-brand-purple' 
                  : 'text-body-grey hover:text-brand-purple'
              } px-3 py-2 text-body font-body font-medium transition-colors relative group`}
            >
              Home
              {isActive('/') && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent-gradient"></span>
              )}
            </Link>
            <Link
              to="/templates"
              className={`${
                isActive('/templates') 
                  ? 'text-brand-purple' 
                  : 'text-body-grey hover:text-brand-purple'
              } px-3 py-2 text-body font-body font-medium transition-colors relative group`}
            >
              Templates
              {isActive('/templates') && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent-gradient"></span>
              )}
            </Link>
            <button className="text-body-grey hover:text-brand-purple px-3 py-2 text-body font-body font-medium transition-colors">
              Pricing
            </button>
            <button className="text-body-grey hover:text-brand-purple px-3 py-2 text-body font-body font-medium transition-colors">
              About
            </button>
          </nav>
          
          {/* Desktop CTA */}
          <div className="hidden md:flex items-center space-x-4">
            <Link
              to="/login"
              className="px-4 py-2 text-body-grey hover:text-text-dark font-body font-medium transition-colors"
            >
              Sign In
            </Link>
            <Link
              to="/signup"
              className="bg-accent-gradient text-white px-6 py-2.5 rounded-lg text-button font-body font-medium hover:shadow-lg transform hover:scale-105 transition-all duration-200"
            >
              Get Started
            </Link>
          </div>
          
          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-body-grey" />
            ) : (
              <Menu className="w-6 h-6 text-body-grey" />
            )}
          </button>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
          <div className="px-6 py-4 space-y-2">
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block px-4 py-2 rounded-lg ${
                isActive('/') ? 'bg-primary-50 text-brand-purple' : 'text-body-grey hover:bg-gray-50'
              } font-body font-medium`}
            >
              Home
            </Link>
            <Link
              to="/templates"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block px-4 py-2 rounded-lg ${
                isActive('/templates') ? 'bg-primary-50 text-brand-purple' : 'text-body-grey hover:bg-gray-50'
              } font-body font-medium`}
            >
              Templates
            </Link>
            <button className="block w-full text-left px-4 py-2 rounded-lg text-body-grey hover:bg-gray-50 font-body font-medium">
              Pricing
            </button>
            <button className="block w-full text-left px-4 py-2 rounded-lg text-body-grey hover:bg-gray-50 font-body font-medium">
              About
            </button>
            <div className="mt-4 space-y-2">
              <Link
                to="/login"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-lg text-center font-body font-medium hover:bg-gray-50"
              >
                Sign In
              </Link>
              <Link
                to="/signup"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full bg-accent-gradient text-white px-4 py-3 rounded-lg text-center font-body font-medium"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;